import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Check, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CheckoutPage = () => {
  const { plan } = useParams();
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [planDetails, setPlanDetails] = useState(null);

  useEffect(() => {
    fetchPlanDetails();
  }, [plan]);

  const fetchPlanDetails = async () => {
    try {
      const response = await axios.get(`${API}/subscription/plans`);
      const selected = response.data.plans.find(p => p.id === plan);
      setPlanDetails(selected);
    } catch (error) {
      toast.error('Échec du chargement des détails du plan');
    }
  };

  const handleCheckout = async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const response = await axios.post(
        `${API}/subscription/checkout?plan_id=${plan}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Paiement réussi (Démo)!');
      setTimeout(() => navigate('/dashboard'), 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Échec du paiement');
    } finally {
      setLoading(false);
    }
  };

  if (!planDetails) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-teal-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#2E59D7]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-teal-50 p-6 flex items-center justify-center">
      <Card className="w-full max-w-2xl p-8 shadow-2xl" data-testid="checkout-card">
        <h1 className="text-3xl font-bold text-center text-gray-900 mb-2">Finaliser votre achat</h1>
        <p className="text-center text-gray-600 mb-8">Vous souscrivez au plan {planDetails.name}</p>

        <div className="bg-gray-50 rounded-lg p-6 mb-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Plan {planDetails.name}</h2>
            <div className="text-right">
              <p className="text-3xl font-bold text-gray-900">{planDetails.price}€</p>
              <p className="text-gray-600">par mois</p>
            </div>
          </div>

          <div className="space-y-3">
            {planDetails.features.map((feature, index) => (
              <div key={index} className="flex items-center gap-3">
                <Check className="w-5 h-5 text-green-600" />
                <span className="text-gray-700">{feature}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-8" data-testid="mock-notice">
          <p className="text-sm text-yellow-800">
            <strong>Paiement de démonstration :</strong> Ceci est une démo. Aucun paiement réel ne sera traité.
          </p>
        </div>

        <div className="space-y-4">
          <Button 
            onClick={handleCheckout} 
            className="w-full bg-gradient-to-r from-[#2E59D7] to-[#0082FB] hover:from-[#0061A0] hover:to-[#2E59D7] text-lg py-6" 
            disabled={loading}
            data-testid="complete-checkout-button"
          >
            <CreditCard className="w-5 h-5 mr-2" />
            {loading ? 'Traitement...' : `Payer ${planDetails.price}€/mois`}
          </Button>

          <Button 
            variant="outline" 
            onClick={() => navigate('/dashboard')} 
            className="w-full"
            data-testid="cancel-button"
          >
            Annuler
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default CheckoutPage;